import { useTranslation } from 'react-i18next'
import { buttonVariants } from '@/shared/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'

export interface ConfirmDialogProps {
  open: boolean
  /**
   * Extra content rendered between the description and the buttons. Use it when the
   * decision needs something to act on — a value to copy, a list to read — that would
   * otherwise sit on the page behind the dialog, where the overlay makes it
   * unclickable.
   */
  children?: React.ReactNode
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'destructive' | 'default'
  onConfirm: () => void
  loading?: boolean
  confirmDisabled?: boolean
  /**
   * Keep the dialog open when the confirm button is clicked, handing the
   * decision to close to the caller (F120).
   *
   * Radix's `AlertDialogAction` closes on click, which is right for the usual
   * one-shot confirmation — a click that only has to be deliberate, with
   * nothing inside the dialog to lose if the request is then refused. It is
   * wrong for a dialog that *collects* something: the two second-factor
   * removals ask for the account password, and a mistyped one used to take the
   * dialog and the half-typed password with it, leaving a toast over the bare
   * settings page and a full re-open to try again.
   *
   * Opt in, and closing becomes the caller's job — in practice the mutation's
   * `onSuccess`, which is the only place that knows the act actually happened.
   * Cancel and the overlay are unaffected either way.
   */
  keepOpenOnConfirm?: boolean
  /**
   * Marks the confirm button so a test can assert a specific dialog intervened, without
   * asserting on its copy. The dialog is shared, so its default labels identify nothing.
   */
  confirmTestId?: string
}

export function ConfirmDialog({
  open,
  children,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'default',
  onConfirm,
  loading = false,
  confirmDisabled = false,
  keepOpenOnConfirm = false,
  confirmTestId,
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  // Defaults fall through to translated values so existing callsites that
  // don't pass labels automatically get localised Confirm/Cancel buttons.
  const resolvedConfirm = confirmLabel ?? t('button.confirm')
  const resolvedCancel  = cancelLabel  ?? t('button.cancel')
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {/* The text and `children` scroll; the buttons stay pinned (RB-37: the confirm tier is
          height-limited, so a long body must not push the buttons off-screen). */}
      <AlertDialogContent size="confirm" className="flex flex-col overflow-hidden">
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            {description && (
              <AlertDialogDescription>{description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          {children}
        </div>
        {/* Mobile: full-width stacked (confirm on top); desktop: inline right-aligned */}
        <AlertDialogFooter className="shrink-0 flex-col-reverse gap-2 sm:flex-row sm:gap-0 sm:space-x-2">
          <AlertDialogCancel disabled={loading} className="w-full sm:w-auto mt-0">
            {resolvedCancel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={confirmTestId}
            onClick={(e) => {
              if (keepOpenOnConfirm) e.preventDefault()
              onConfirm()
            }}
            disabled={loading || confirmDisabled}
            className={`w-full sm:w-auto ${variant === 'destructive' ? buttonVariants({ variant: 'destructive' }) : ''}`}
          >
            {loading ? t('common.pleaseWait') : resolvedConfirm}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
